// RewardsPage — iOS HIG with "Platform Activation Bonuses" sheet hero + reward detail
import { BrandLogo } from '../../components/Brand';
// Reference: sheet showing 3 floating brand circles, "Platform Activation Bonuses" title,
// countdown timer pill, "Redeem now" black button. Detail shows platform info, unlock
// date, reward value, shares received, price at reward, "Invite friends, earn more" card.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Empty, ErrorBox, FormField, PrimaryButton, SecondaryButton } from './UIShell';

interface Reward {
  id: string;
  brand: string;
  brandColor: string;
  platform: { name: string; symbol: string; exchange: string };
  unlock_date: string;
  reward_value: number;
  shares: number;
  price_at_reward: number;
  reward_type: 'Activation' | 'Referral' | 'Bonus';
}

const MOCK_REWARDS: Reward[] = [
  {
    id: 'rwd-1',
    brand: 'Deepchart',
    brandColor: '#1C1C1E',
    platform: { name: 'Deepchart Pro', symbol: 'DCP', exchange: 'FunderPro' },
    unlock_date: '2026-01-05',
    reward_value: 30.00,
    shares: 1,
    price_at_reward: 30.00,
    reward_type: 'Activation',
  },
  {
    id: 'rwd-2',
    brand: 'ATAS',
    brandColor: '#76B900',
    platform: { name: 'ATAS Pro', symbol: 'ATP', exchange: 'FunderPro' },
    unlock_date: '2026-02-15',
    reward_value: 25.00,
    shares: 1,
    price_at_reward: 25.00,
    reward_type: 'Referral',
  },
  {
    id: 'rwd-3',
    brand: 'Quantower',
    brandColor: '#FF9900',
    platform: { name: 'Quantower Pro', symbol: 'QTP', exchange: 'FunderPro' },
    unlock_date: '2026-03-10',
    reward_value: 20.00,
    shares: 1,
    price_at_reward: 20.00,
    reward_type: 'Bonus',
  },
];

export function RewardsPage() {
  const nav = useNavigate();
  const [selected, setSelected] = useState<Reward | null>(null);
  const [email, setEmail] = useState('');

  // If a reward is selected, show its detail
  if (selected) {
    return <RewardDetail reward={selected} onBack={() => setSelected(null)} onInvite={() => alert('Invite link copied!')} />;
  }

  return (
    <div className="space-y-4 p-4">
      {/* Title */}
      <h1 className="px-1 text-[34px] font-bold leading-[1.1] tracking-tight text-tf-text-1">Rewards</h1>
      <p className="px-1 text-[15px] text-tf-text-3">Activation bonuses and platform rewards</p>

      {/* Hero sheet — Platform Activation Bonuses */}
      <div className="relative overflow-hidden rounded-[24px] bg-white p-6 text-center shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
        <div className="relative mx-auto mb-4 h-32 w-32">
          {MOCK_REWARDS.map((r, i) => {
            const angle = (i * 120 - 90) * (Math.PI / 180);
            const radius = 50;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            return (
              <button
                key={r.id}
                onClick={() => setSelected(r)}
                className="absolute flex h-12 w-12 items-center justify-center rounded-full text-white shadow-lg transition active:scale-90"
                style={{ background: r.brandColor, transform: `translate(${x}px, ${y}px)`, animation: `float-${i} 3s ease-in-out infinite ${i * 0.3}s` }}
                data-testid={`reward-orb-${r.brand.toLowerCase()}`}
              >
                <BrandLogo name={r.brand} size={48} rounded className="shadow-lg" />
              </button>
            );
          })}
          <div className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-tf-text-1/10" />
        </div>
        <h2 className="mt-2 text-[22px] font-bold text-tf-text-1">Platform Activation Bonuses</h2>
        <p className="mt-1 text-[14px] text-tf-text-3">Redeem now and get $22 platform bonuses.</p>
        <p className="mt-0.5 text-[13px] text-tf-text-3">Platform rewards are credited instantly.</p>
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-tf-surface-1 px-3 py-1.5 text-[13px] font-semibold tabular-nums text-tf-text-1" data-testid="reward-countdown">
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" strokeLinecap="round" /></svg>
          1d 23h 59m 34s
        </div>
        <div className="mt-4">
          <PrimaryButton onClick={() => alert('Platform activated! Bonus credited to your account.')} testId="reward-redeem-now">
            Redeem now
          </PrimaryButton>
        </div>
      </div>

      {/* Individual reward cards */}
      <div>
        <h3 className="px-1 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Your rewards</h3>
        <div className="overflow-hidden rounded-[16px] border border-tf-border bg-white">
          {MOCK_REWARDS.map((r) => (
            <button
              key={r.id}
              onClick={() => setSelected(r)}
              data-testid={`reward-card-${r.id}`}
              className="flex w-full items-center gap-3 border-b border-tf-border-strong/55 bg-white px-4 py-3 text-left active:bg-[#E5E5EA]/60 last:border-b-0"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full text-white" style={{ background: r.brandColor }}>
                <BrandLogo name={r.brand} size={48} rounded className="shadow-lg" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[15px] font-semibold text-tf-text-1">{r.platform.name} ({r.platform.symbol})</div>
                <div className="truncate text-[12px] text-tf-text-3">{r.reward_type} reward · {new Date(r.unlock_date).toLocaleDateString()}</div>
              </div>
              <div className="text-right">
                <div className="text-[14px] font-semibold tabular-nums text-tf-text-1">${r.reward_value.toFixed(2)}</div>
                <div className="text-[12px] text-tf-profit">Ready</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Refer-a-friend card */}
      <div className="rounded-[16px] border border-tf-border bg-white p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tf-blue/10 text-tf-blue">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
          </div>
          <div className="flex-1">
            <div className="text-[15px] font-semibold text-tf-text-1">Invite friends, earn more</div>
            <p className="mt-0.5 text-[13px] text-tf-text-3">Get free platform bonuses when your friends start investing.</p>
          </div>
          <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
      </div>
    </div>
  );
}

function RewardDetail({ reward, onBack, onInvite }: { reward: Reward; onBack: () => void; onInvite: () => void }) {
  return (
    <div className="pb-[calc(120px+env(safe-area-inset-bottom))]">
      {/* Top bar */}
      <div className="sticky top-14 z-20 flex items-center justify-between bg-tf-surface-1 px-4 pb-2 pt-1">
        <button onClick={onBack} data-testid="reward-back" className="flex h-9 w-9 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="flex items-center gap-2">
          <button className="flex h-9 w-9 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100" aria-label="Share">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <button className="flex h-9 w-9 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100" aria-label="More">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>
          </button>
        </div>
      </div>

      <div className="px-4">
        {/* Hero */}
        <div className="rounded-[24px] bg-white p-6 text-center shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full text-white" style={{ background: reward.brandColor }}>
            <BrandLogo name={reward.brand} size={48} rounded className="shadow-lg" />
          </div>
          <h1 className="mt-3 text-[24px] font-bold text-tf-text-1">{reward.platform.name}</h1>
          <p className="mt-1 text-[15px] text-tf-text-3">Bonus unlocked!</p>
        </div>

        {/* Details list */}
        <div className="mt-3 overflow-hidden rounded-[16px] bg-white">
          <DetailRow label="Platform" value={`${reward.platform.name} (${reward.platform.symbol})`} />
          <DetailRow label="Unlock date" value={new Date(reward.unlock_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
          <DetailRow label="Reward value" value={`$${reward.reward_value.toFixed(2)}`} />
          <DetailRow label="Quantity" value={`≈ ${reward.shares.toFixed(3)} ${reward.platform.symbol}`} last />
          <DetailRow label="Bonus value" value={`$${reward.price_at_reward.toFixed(2)}`} />
          <DetailRow label="Reward type" value={reward.reward_type} last />
        </div>

        {/* Invite card */}
        <div className="mt-3 rounded-[16px] border border-tf-border bg-white p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tf-blue/10 text-tf-blue">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
            </div>
            <div className="flex-1">
              <div className="text-[15px] font-semibold text-tf-text-1">Invite friends, earn more</div>
              <p className="mt-0.5 text-[13px] text-tf-text-3">Get free platform bonuses when your friends start investing.</p>
            </div>
            <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="fixed inset-x-0 bottom-[56px] z-30 border-t border-gray-200 bg-white px-4 py-3" style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}>
        <div className="mx-auto max-w-[640px]">
          <PrimaryButton onClick={() => alert(`Claimed $${reward.reward_value.toFixed(2)} ${reward.platform.symbol} reward!`)} testId="reward-claim">
            Claim ${reward.reward_value.toFixed(2)} reward
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between px-4 py-3', last ? '' : 'border-b border-tf-border-strong/55'].join(' ')}>
      <span className="text-[15px] text-tf-text-1">{label}</span>
      <span className="text-[15px] font-semibold tabular-nums text-tf-text-1">{value}</span>
    </div>
  );
}
